import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntl } from 'react-intl';
import { useGetOnDemandCirclesQuery } from '@modernhealth/circles-graphql/v2';

import { BackButton } from '../components/BackButton';
import { CircleOnDemandCard, CircleOnDemandCardSkeleton } from '../components/CircleOnDemandCard';
import { CircleOnDemandPageSection } from '../components/CircleOnDemandPageSection';
import { Page } from '../components/PageLayout';
import { CircleCenteredBanner } from '../components/CircleCenteredBanner';
import { languageCodes } from '../utils/languageCodes';

export const CirclesOnDemandOverviewPage = () => {
    const { t } = useTranslation('circles');

    return (
        <Page.Container>
            <Page.Header>
                <BackButton>
                    {previousRoute => {
                        if (previousRoute === '/circles') {
                            return t('routes.fallback.title');
                        }

                        return null;
                    }}
                </BackButton>
            </Page.Header>

            <Page.Content
                css={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 40,
                }}
            >
                <CircleCenteredBanner
                    header={t('pages.on_demand.hero.title')}
                    description={t('pages.on_demand.hero.description')}
                />

                {languageCodes.map(languageCode => {
                    return (
                        <CirclesOnDemandByLanguageSection
                            key={languageCode}
                            languageCode={languageCode}
                            cta={t('sections.series.view_all')}
                            to={`/circles/language/${languageCode}`}
                        />
                    );
                })}
            </Page.Content>
        </Page.Container>
    );
};

export function useTranslatedLanguage(languageCode: string, fallback: string) {
    const intl = useIntl();
    const language = useMemo(() => {
        return intl.formatDisplayName(languageCode, {
            type: 'language',
        });
    }, [intl, languageCode]);

    return language ?? fallback;
}

const CirclesOnDemandByLanguageSectionSkeleton: React.FC<{ languageCode: string }> = ({
    languageCode,
}) => {
    const header = useTranslatedLanguage(languageCode, languageCode);

    return (
        <CircleOnDemandPageSection header={header}>
            <CircleOnDemandCardSkeleton />
            <CircleOnDemandCardSkeleton />
            <CircleOnDemandCardSkeleton />
        </CircleOnDemandPageSection>
    );
};

export const CirclesOnDemandByLanguageSection: React.FC<{
    languageCode: string;
    heading?: string;
    cta?: string;
    to?: string;
}> = ({ languageCode, heading, cta, to }) => {
    const { data, loading } = useGetOnDemandCirclesQuery({
        variables: {
            filterByLanguageCodes: [languageCode],
        },
    });

    const header = useTranslatedLanguage(languageCode, languageCode);

    if (loading) {
        return <CirclesOnDemandByLanguageSectionSkeleton languageCode={languageCode} />;
    }

    if (data?.circleOnDemandConnection.__typename !== 'CircleOnDemandConnection') {
        return null;
    }

    const circles = data.circleOnDemandConnection.edges;
    // hide the language header/section if no ondemand videos available
    if (!circles || !circles.length) return null;
    return (
        <CircleOnDemandPageSection header={heading ?? header} cta={cta} to={to}>
            {circles.map((edge, i) => {
                const ondemand = edge?.node;
                if (!ondemand) return null;

                return (
                    <CircleOnDemandCard
                        key={`ondemand-${ondemand.id}-${i}`}
                        seconds={ondemand.video.durationInSeconds}
                        host={ondemand.facilitators[0]?.name}
                        languageCode={ondemand.languageCode}
                        thumb={ondemand.video.thumbnail}
                        name={ondemand.name}
                        slug={ondemand.slug}
                    />
                );
            })}
        </CircleOnDemandPageSection>
    );
};
